import { ALTERSKLASSEN, WILDARTEN_MIT_ALTERSKLASSE, type Schussausgang } from "@dj/domain/schuss";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Kachel } from "../design/Kachel";
import { fensterOffen, useRueckgaengig, type Angabe } from "../design/Rueckgaengig";
import { useSperre } from "../design/sperre";
import { Symbol } from "../design/Symbol";
import { WILDARTEN_IM_ABLAUF } from "../design/wildarten";
import { umschlag } from "../plattform/api";
import { useDokument } from "../plattform/lesemodelle";
import { useSitzung } from "../plattform/sitzung";
import { einreihen } from "../plattform/warteschlange";
import { positionMessen, type Messung } from "./geraeteposition";
import { symbolDer, useRevierSammlung, type Rahmen } from "./lage";

/**
 * Ablauf „Schuss“ (oberflaeche.md §5, design/README §9.6, §10.3; F-120 … F-123): Wildart → [Altersklasse] →
 * Schussausgang → [Fluchtrichtung], ohne Absenden. Stand, Schütze und Zeitpunkt setzt der Server bzw. das Gerät, die
 * Schützenposition misst das Gerät beim Öffnen des Ablaufs (LIM-0009; ohne Position wird trotzdem gemeldet).
 *
 * „Stück liegt“ und „Fehlschuss“ melden sofort (Rückgängig-Fenster, ADR-0023). „Anschuss“ meldet ebenfalls sofort —
 * ein Anschuss darf nie an einer fehlenden Fluchtrichtung hängen bleiben (`schuss/03`) — und zeigt danach die
 * Fluchtrichtung: Ein Pfeil ergänzt die noch offene Meldung; ist ihr Fenster schon abgelaufen, geht die Fluchtrichtung
 * als Ergänzung (`SchussErgaenzen`) in die Warteschlange. „Zurück“ in der Fluchtrichtung nimmt die Meldung zurück.
 */
type Schritt = "wildart" | "andere" | "altersklasse" | "ausgang" | "flucht";

/** 3 × 3 der Fluchtrichtung, Nord oben (design/README §9.7); `null` = eigener Stand in der Mitte */
const RASTER: Array<string | null> = ["NW", "N", "NO", "W", null, "O", "SW", "S", "SO"];
const AUSGANGSSYMBOL: Record<Schussausgang, string> = { "Stück liegt": "stueck-liegt", Anschuss: "anschuss", Fehlschuss: "fehlschuss" };

export function SchussAblauf({ rahmen }: { rahmen: Rahmen }) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { uid } = useSitzung();
  const rg = useRueckgaengig();
  const eintrag = useDokument(uid ? `personen/${uid}/meine_drueckjagden/${id}` : null);
  const revier = (eintrag.daten?.["_kennungen"] as Record<string, string> | undefined)?.["Revier"] ?? null;
  const einweisung = useDokument(revier ? `drueckjagden/${id}/reviere/${revier}/einweisung/aktuell` : null);
  const aufgaben = useDokument(uid ? `drueckjagden/${id}/meine_aufgaben/${uid}` : null);
  const freigabe = useRevierSammlung(id, revier, "freigabe");
  const kontingente = useRevierSammlung(id, revier, "kontingentstand");
  const [schritt, setSchritt] = useState<Schritt>("wildart");
  const [wildart, setWildart] = useState<string | null>(null);
  const [altersklasse, setAltersklasse] = useState<string | null>(null);
  const [gemeldet, setGemeldet] = useState<string | null>(null);
  const messung = useRef<Promise<Messung | null> | null>(null);
  useEffect(() => { messung.current = positionMessen(); }, []);
  const gesperrt = useSperre("ablauf", schritt);
  // Die Auswahl steht erst, wenn die Wildarten der Drückjagd bekannt sind: sonst wechselten die Kacheln nachträglich.
  const bekannt = (!!eintrag.daten || !eintrag.laedt) && (!revier || !!einweisung.daten || !einweisung.laedt);

  const stand = aufgaben.daten?.["Stand"] as string | null | undefined;
  const wildartenDerJagd = ((einweisung.daten?.["Wildarten"] as string[] | undefined) ?? [...WILDARTEN_IM_ABLAUF]);
  const sichtbar = WILDARTEN_IM_ABLAUF.filter((w) => wildartenDerJagd.includes(w));
  const ausgeblendet = WILDARTEN_IM_ABLAUF.filter((w) => !wildartenDerJagd.includes(w));
  const gesperrteWildart = (w: string) => freigabe.some((f) => f["Wildart"] === w && f["Freigabestatus"] === "gesperrt");
  const gesperrteAnsprache = (w: string, ak: string) => gesperrteWildart(w)
    || kontingente.some((k) => k["Wildart"] === w && ((k["Ansprachen"] as string[] | undefined) ?? []).includes(ak) && k["Status"] !== "frei");
  const mitAltersklasse = !!wildart && (WILDARTEN_MIT_ALTERSKLASSE as readonly string[]).includes(wildart);
  const schritte = 3 + (mitAltersklasse ? 1 : 0) + (schritt === "flucht" ? 1 : 0);
  const nummer = { wildart: 1, andere: 1, altersklasse: 2, ausgang: mitAltersklasse ? 3 : 2, flucht: mitAltersklasse ? 4 : 3 }[schritt];
  const titel = schritt === "wildart" || schritt === "andere" ? "Wildart" : schritt === "flucht" ? "Fluchtrichtung" : [wildart, altersklasse].filter(Boolean).join(" ");
  const zurStartansicht = () => navigate(`/drueckjagd/${id}`, { replace: true });

  // Ohne gewählte Fluchtrichtung endet der Ablauf mit dem Rückgängig-Fenster des Anschusses: Die Meldung ist dann
  // abgeschlossen, die Fluchtrichtung bleibt eine Ergänzung in „Meldungen“ (oberflaeche.md §8 Nr. 2).
  const fensterGesehen = useRef(false);
  useEffect(() => {
    if (schritt !== "flucht" || !gemeldet) {
      fensterGesehen.current = false;
      return;
    }
    if (fensterOffen(rg.offen, gemeldet)) fensterGesehen.current = true;
    else if (fensterGesehen.current) zurStartansicht();
  }, [schritt, gemeldet, rg.offen]);

  const wildartWaehlen = (w: string) => {
    setWildart(w);
    setAltersklasse(null);
    setSchritt((WILDARTEN_MIT_ALTERSKLASSE as readonly string[]).includes(w) ? "altersklasse" : "ausgang");
  };

  const angabenDes = (ausgang: Schussausgang): Angabe[] => [
    [symbolDer(wildart ?? "", altersklasse), [wildart, altersklasse].filter(Boolean).join(" ")], [AUSGANGSSYMBOL[ausgang], ausgang],
  ];

  const melden = (ausgang: Schussausgang) => {
    if (!wildart) return;
    const angaben = angabenDes(ausgang);
    const daten: Record<string, unknown> = { "Drückjagd": id, Wildart: wildart, Schussausgang: ausgang, ...(altersklasse ? { Altersklasse: altersklasse } : {}) };
    const u = rg.vormerken({ befehl: "SchussMelden", begriff: "Schuss", symbol: "schuss", angaben, titel: (x) => `Schuss ${x.meldungsId}`, daten });
    // Die Schützenposition kommt, sobald das Gerät sie liefert; ohne Position bleibt die Meldung ohne (LIM-0009).
    void messung.current?.then((m) => {
      if (m) rg.ergaenzen(u.meldungsId, { "Schützenposition": m.position, "Genauigkeit Schützenposition": m.genauigkeit });
    });
    if (ausgang === "Anschuss") {
      setGemeldet(u.meldungsId);
      setSchritt("flucht");
    } else {
      zurStartansicht();
    }
  };

  const fluchtrichtung = (r: string) => {
    if (!gemeldet) return;
    if (!rg.ergaenzen(gemeldet, { Fluchtrichtung: r }, [...angabenDes("Anschuss"), ["fluchtrichtung", r]])) {
      void einreihen("SchussErgaenzen", `Ergänzung zum Schuss ${gemeldet}`, umschlag({ Schuss: gemeldet, Fluchtrichtung: r }));
    }
    zurStartansicht();
  };

  const zurueck = () => {
    if (schritt === "wildart") zurStartansicht();
    else if (schritt === "andere" || (schritt === "ausgang" && !mitAltersklasse)) setSchritt("wildart");
    else if (schritt === "altersklasse") setSchritt("wildart");
    else if (schritt === "ausgang") setSchritt("altersklasse");
    else {
      fensterGesehen.current = false;
      if (gemeldet && rg.offen?.umschlag.meldungsId === gemeldet) rg.zuruecknehmen();
      setGemeldet(null);
      setSchritt("ausgang");
    }
  };

  const hinweis = (symbol: string, begriff: string) => (
    <div className="hinweis platte" data-zeile-fest="">
      <Symbol name={symbol} />
      <div><b>{begriff}</b><span>{[stand ? `Stand ${stand}` : null].filter(Boolean).join(" · ") || "Schuss"}</span></div>
    </div>
  );

  if (!bekannt) return <div className="ansicht" aria-busy="true" />;
  const klassen = schritt === "ausgang" ? "ablauf--ausgang" : schritt === "flucht" ? "ablauf--flucht" : true;
  return rahmen({
    drueckjagdId: id, name: "Schuss", ablauf: klassen,
    titel, schritte: `Schuss · ${nummer} von ${schritte}`, sperre: gesperrt,
    children: (
      <>
        <Kachel symbol="zurueck" begriff="Zurück" art="kompakt" className="zurueck-oben" onPress={zurueck} />
        {schritt === "wildart" ? (
          <>
            <div className="r12">
              <div className="raster raster--2x3" data-gleich="">
                {sichtbar.map((w) => <Kachel key={w} symbol={symbolDer(w)} begriff={w} art="wahl" fuge chip={gesperrteWildart(w) ? "gesperrt" : null} onPress={() => wildartWaehlen(w)} />)}
                {ausgeblendet.length ? <Kachel symbol="andere-wildart" begriff="andere Wildart" art="wahl" onPress={() => setSchritt("andere")} /> : null}
              </div>
            </div>
            <div className="r3">{hinweis("schuss", "Schuss")}</div>
          </>
        ) : null}
        {schritt === "andere" ? (
          <>
            <div className="r12">
              <div className="raster raster--2x2" data-gleich="">
                {ausgeblendet.map((w) => <Kachel key={w} symbol={symbolDer(w)} begriff={w} art="wahl" fuge onPress={() => wildartWaehlen(w)} />)}
              </div>
            </div>
            <div className="r3">{hinweis("andere-wildart", "andere Wildart")}</div>
          </>
        ) : null}
        {schritt === "altersklasse" && wildart ? (
          <>
            <div className="r12">
              <div className="raster raster--2x2 raster--alter" data-gleich="">
                {ALTERSKLASSEN.map((ak) => (
                  <Kachel key={ak} symbol={symbolDer(wildart, ak)} begriff={ak} art="wahl" chip={gesperrteAnsprache(wildart, ak) ? "gesperrt" : null}
                    onPress={() => { setAltersklasse(ak); setSchritt("ausgang"); }} />
                ))}
              </div>
            </div>
            <div className="r3">{hinweis(symbolDer(wildart), wildart)}</div>
          </>
        ) : null}
        {schritt === "ausgang" && wildart ? (
          <>
            <div className="r12">
              <Kachel symbol="anschuss" begriff="Anschuss" art="wahl" className="k--breit" onPress={() => melden("Anschuss")} />
              {hinweis(symbolDer(wildart, altersklasse), [wildart, altersklasse].filter(Boolean).join(" "))}
            </div>
            <div className="r3">
              <div className="raster raster--2" data-gleich="">
                <Kachel symbol="stueck-liegt" begriff="Stück liegt" art="wahl" onPress={() => melden("Stück liegt")} />
                <Kachel symbol="fehlschuss" begriff="Fehlschuss" art="wahl" onPress={() => melden("Fehlschuss")} />
              </div>
            </div>
          </>
        ) : null}
        {schritt === "flucht" ? (
          <>
            <div className="r12">{hinweis("anschuss", "Anschuss")}</div>
            <div className="r3">
              <div className="raster raster--3x3">
                {RASTER.map((r) => (r ? (
                  <Kachel key={r} symbol="fluchtrichtung" begriff={r} art="wahl" className={`k--richtung k--richtung-${r}`} onPress={() => fluchtrichtung(r)} />
                ) : (
                  <div key="mitte" className="flucht__mitte platte" data-zeile-fest=""><Symbol name="stand" /><span>{stand ? `Stand ${stand}` : "Stand"}</span></div>
                )))}
              </div>
            </div>
          </>
        ) : null}
      </>
    ),
  });
}
